import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Ship, Plane, TrendingUp, CheckCircle2, Globe, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { TransportSection } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
interface TransportSectionsProps {
  sections: TransportSection[];
}
const getIcon = (type: TransportSection['type']) => {
  switch (type) {
    case 'road':
      return <Truck className="w-5 h-5 text-primary" />;
    case 'ocean':
      return <Ship className="w-5 h-5 text-primary" />;
    case 'air':
      return <Plane className="w-5 h-5 text-primary" />;
    case 'macro':
      return <TrendingUp className="w-5 h-5 text-primary" />;
    default:
      return <Globe className="w-5 h-5 text-primary" />;
  }
};
const getPlaceholderImage = (type: TransportSection['type']) => {
  switch (type) {
    case 'road':
      return IMAGES.ROAD_FREIGHT_3;
    case 'ocean':
      return IMAGES.OCEAN_FREIGHT_2;
    case 'air':
      return IMAGES.AIR_FREIGHT_3;
    default:
      return IMAGES.LOGISTICS_HERO_1;
  }
};
const getBadgeLabel = (type: TransportSection['type']) => {
  switch (type) {
    case 'road':
      return 'Road Freight';
    case 'ocean':
      return 'Ocean Logistics';
    case 'air':
      return 'Air Cargo';
    case 'macro':
      return 'Macro Economics';
    default:
      return 'Market Analysis';
  }
};
export function TransportSections({
  sections
}: TransportSectionsProps) {
  // Only filter for road, ocean, air for the main frames as requested
  const mainSections = sections.filter(s => ['road', 'ocean', 'air'].includes(s.type));
  return <section id="transport-insights" className="w-full py-12 bg-background">
      <div className="container mx-auto px-4">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {mainSections.map(section => <motion.div key={section.id} variants={staggerItem}>
              <Card className="h-full overflow-hidden border-border bg-card hover:shadow-xl transition-all duration-300 group">
                <div className="relative h-48 overflow-hidden">
                  <img src={section.image || getPlaceholderImage(section.type)} alt={section.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className="p-2 bg-primary/10 backdrop-blur-md rounded-lg border border-primary/20">
                      {getIcon(section.type)}
                    </div>
                    <Badge variant="secondary" className="bg-primary/90 text-primary-foreground">
                      {getBadgeLabel(section.type)}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="pb-2">
                  <CardTitle className="text-xl font-bold text-foreground">
                    {section.title}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {section.summary}
                  </p>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Key Insights
                    </h4>
                    <ul className="space-y-2">
                      {section.highlights.map((highlight, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-foreground/90">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
                          <span>{highlight}</span>
                        </li>)}
                    </ul>
                  </div>

                  {section.metrics && section.metrics.length > 0 && <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                      {section.metrics.map((metric, mIdx) => <div key={mIdx} className="flex flex-col">
                          <span className="text-xs text-muted-foreground">{metric.label}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-lg font-mono font-bold">
                              {metric.value}
                            </span>
                            {metric.isPositive !== undefined && (metric.isPositive ? <ArrowUpRight className="w-3 h-3 text-emerald-500" /> : <ArrowDownRight className="w-3 h-3 text-destructive" />)}
                          </div>
                        </div>)}
                    </div>}

                  <div className="pt-4 flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground uppercase">
                      Source: {section.source}
                    </span>
                    
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}